import { useQuery } from '@tanstack/react-query';
import api from '../lib/api.js';
import { useScopeKey } from './useScopeKey.js';

/**
 * Récupère le détail d'une cellule cash-flow : transactions passées (actuals)
 * et/ou items prévisionnels (forecasts) sur une fenêtre catégorie × période.
 *
 * Réponse attendue :
 *   {
 *     window: { from, to },
 *     actuals:   [{ id, label, amount_cents, settled_at, category_id, vat_rate, vat_amount_cents }],
 *     forecasts: [{ mode, id, label, amount_cents, date, category_id, vat_rate, amount_cents_vat }],
 *     totals: { actuals_cents, forecasts_cents, grand_cents, total_ht_cents, total_vat_cents,
 *               actuals_ht_cents, actuals_vat_cents, forecasts_ht_cents, forecasts_vat_cents }
 *   }
 *
 * Props :
 *   from        : ISO date "YYYY-MM-DD"
 *   to          : ISO date "YYYY-MM-DD"
 *   categoryIds : string[]
 *   scenario    : 'median' | 'pessimiste' | 'optimiste' (défaut 'median')
 *   vatFilter   : 'collected' | 'deductible' | null — drill-down sur les lignes TVA
 *                 (quand présent, categoryIds est ignoré côté backend)
 *
 * Désactivé si from/to manquant ou si ni categoryIds ni vatFilter ne sont fournis.
 */
export function useCashFlowBreakdown({ from, to, categoryIds = [], scenario = 'median', vatFilter = null }) {
  const scopeKey = useScopeKey();
  const enabled = !!from && !!to && (categoryIds.length > 0 || !!vatFilter) && scopeKey !== 'loading';

  return useQuery({
    queryKey: ['cash-flow', 'breakdown', scopeKey, from, to, categoryIds.join(','), scenario, vatFilter],
    queryFn: () => {
      const params = new URLSearchParams({ from, to, scenario });
      for (const id of categoryIds) params.append('category_ids[]', id);
      if (vatFilter) params.append('vat_filter', vatFilter);
      return api.get(`/cash-flow/breakdown?${params.toString()}`);
    },
    enabled,
    staleTime: 30_000,
  });
}
